import React from "react";
import { WordRestrictions } from "../../../../models/extension/WordExtention";
import { TrainingCourseAssetType } from "../../../../models/interfaces/education/TrainingCourseAssetType";
import TrainingCourseSessionView, {
  TrainingCourseSessionAssetView,
} from "../../../../models/views/education/TrainingCourseSessionView";
import TrainingCourseSessionService from "../../../../services/education/TrainingCourseSessionService";
import NotFoundDataInDate from "../../../shared/not-found-data-in-date/NotFoundDataInDate";
import {
  DEFAULT_COURSE_IMAGE,
  resolveCourseFileUrl,
} from "../course-note.data";
import "../CourseNote.scss";
import CourseNoteSessionsState, {
  CourseNoteSessionsProps,
} from "./CourseNoteSessions.models";
import "./CourseNoteSessions.scss";

export default class CourseNoteSessions extends React.PureComponent<
  CourseNoteSessionsProps,
  CourseNoteSessionsState
> {
  trainingCourseSessionService: TrainingCourseSessionService;
  videoRefs: { [key: number]: HTMLVideoElement | null } = {};

  constructor(props: CourseNoteSessionsProps) {
    super(props);
    this.trainingCourseSessionService = new TrainingCourseSessionService();
    this.state = {
      sessions: [],
      expandedSessionId: null,
      expandedVideoAssetId: null,
      courseTitle: "",
    };
  }

  componentDidMount() {
    this.getSessions();
  }

  componentDidUpdate(prevProps: CourseNoteSessionsProps) {
    if (prevProps.courseNoteId !== this.props.courseNoteId) {
      this.getSessions();
    }
  }

  getSessions = () => {
    const self = this;
    const courseNoteId = this.props.courseNoteId;
    if (courseNoteId == null) return;

    this.trainingCourseSessionService
      .getByCourseNoteId(courseNoteId)
      .then(function (response) {
        const sessions = (response ?? [])
          .filter((session) => session.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        self.setState({
          sessions,
          courseTitle: sessions[0]?.trainingCourseTitle ?? "",
          expandedSessionId: null,
          expandedVideoAssetId: null,
        });
      });
  };

  getAssetIconClass = (assetType: TrainingCourseAssetType) => {
    switch (assetType) {
      case TrainingCourseAssetType.Video:
        return "mdi mdi-play-circle asset-icon-video";
      case TrainingCourseAssetType.Pdf:
      case TrainingCourseAssetType.File:
        return "mdi mdi-file-pdf-box asset-icon-file";
      case TrainingCourseAssetType.Link:
        return "mdi mdi-link-variant asset-icon-quiz";
      case TrainingCourseAssetType.Audio:
        return "mdi mdi-music asset-icon-video";
      case TrainingCourseAssetType.Image:
        return "mdi mdi-image asset-icon-quiz";
      default:
        return "mdi mdi-file-outline";
    }
  };

  getAssetTypeClass = (assetType: TrainingCourseAssetType) => {
    switch (assetType) {
      case TrainingCourseAssetType.Video:
        return "video";
      case TrainingCourseAssetType.Pdf:
      case TrainingCourseAssetType.File:
        return "file";
      case TrainingCourseAssetType.Link:
      case TrainingCourseAssetType.Image:
        return "quiz";
      default:
        return "";
    }
  };

  resolveAssetUrl = (asset: TrainingCourseSessionAssetView) => {
    return resolveCourseFileUrl(asset.fileAttachment);
  };

  hasAssetFile = (asset: TrainingCourseSessionAssetView) => {
    return this.resolveAssetUrl(asset) !== null;
  };

  handleAssetClick = (asset: TrainingCourseSessionAssetView) => {
    const fileUrl = this.resolveAssetUrl(asset);
    if (!fileUrl) return;

    if (
      asset.assetType === TrainingCourseAssetType.Pdf ||
      asset.assetType === TrainingCourseAssetType.File ||
      asset.assetType === TrainingCourseAssetType.Link
    ) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (asset.assetType === TrainingCourseAssetType.Video) {
      this.setState({
        expandedVideoAssetId:
          this.state.expandedVideoAssetId === asset.id ? null : asset.id,
      });
    }
  };

  handleFullscreen = (assetId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const video = this.videoRefs[assetId];
    if (!video) return;

    const anyVideo = video as HTMLVideoElement & {
      webkitRequestFullscreen?: () => void;
      msRequestFullscreen?: () => void;
    };

    if (anyVideo.requestFullscreen) {
      anyVideo.requestFullscreen();
    } else if (anyVideo.webkitRequestFullscreen) {
      anyVideo.webkitRequestFullscreen();
    } else if (anyVideo.msRequestFullscreen) {
      anyVideo.msRequestFullscreen();
    }
  };

  render(): React.ReactNode {
    const { sessions, courseTitle } = this.state;

    return (
      <div
        className="dropdown-list course-note-area course-note-sessions-area"
        style={{ height: "90vh" }}
      >
        {this.courseHeaderView(courseTitle, "")}

        {sessions.length > 0 ? (
          this.sessionsView(sessions)
        ) : (
          <NotFoundDataInDate title="موردی یافت نشد" />
        )}
      </div>
    );
  }

  courseHeaderView(title: string, subtitle: string) {
    return (
      <div className="col-12 notspace margin-top">
        <div className="tk-inline card-boundel course-header-card">
          <div className="col-2 notspace" style={{ display: "flex" }}>
            <img
              className="icon"
              src={DEFAULT_COURSE_IMAGE}
              width="56"
              height="56"
              alt="course icon"
            />
          </div>
          <div
            className="col-10 notspace"
            style={{ textAlign: "right", marginRight: "16px" }}
          >
            <div className="pd-detail">
              <div className="course-title">
                <label>{WordRestrictions(title, 36)}</label>
              </div>
              <div className="course-subtitle">
                <label>{subtitle}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  toggleSession = (session: TrainingCourseSessionView) => {
    if (session.isLock) return;

    this.setState({
      expandedSessionId:
        this.state.expandedSessionId === session.id ? null : session.id,
      expandedVideoAssetId: null,
    });
  };

  sessionsView(sessions: TrainingCourseSessionView[]) {
    const { expandedSessionId } = this.state;

    return (
      <div className="sessions-list">
        {sessions.map((session) => {
          const isExpanded = expandedSessionId === session.id;
          const isLocked = !!session.isLock;

          return (
            <div
              key={session.id}
              className={`session-card card-boundel ${isExpanded ? "expanded" : ""} ${isLocked ? "locked" : ""}`}
            >
              <div
                className="tk-inline session-header"
                aria-disabled={isLocked}
                onClick={() => this.toggleSession(session)}
              >
                <div className="col-2 notspace session-icon">
                  <label className={`mdi ${isLocked ? "mdi-lock" : "mdi-folder"}`}></label>
                </div>
                <div className="col-8 notspace session-title-wrap">
                  <label className="session-title">
                    {WordRestrictions(session.title, 28)}
                  </label>
                </div>
                <div className="col-2 notspace session-arrow">
                  {isLocked ? (
                    <label className="session-private-badge">خصوصی</label>
                  ) : (
                    <label
                      className={`mdi ${isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"}`}
                    ></label>
                  )}
                </div>
              </div>

              {isExpanded && !isLocked ? this.assetsView(session.assets ?? []) : null}
            </div>
          );
        })}
      </div>
    );
  }

  assetsView(assets: TrainingCourseSessionAssetView[]) {
    const { expandedVideoAssetId } = this.state;
    const activeAssets = assets
      .filter((asset) => asset.isActive && (asset.isLock || this.hasAssetFile(asset)))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    if (activeAssets.length === 0) {
      return (
        <div className="session-body">
          <NotFoundDataInDate title="موردی یافت نشد" />
        </div>
      );
    }

    return (
      <div className="session-body">
        {activeAssets.map((asset) => {
          const isLocked = !!asset.isLock;
          const isVideoOpen = !isLocked && expandedVideoAssetId === asset.id;
          const isClickable =
            !isLocked &&
            (asset.assetType === TrainingCourseAssetType.Pdf ||
              asset.assetType === TrainingCourseAssetType.File ||
              asset.assetType === TrainingCourseAssetType.Link ||
              asset.assetType === TrainingCourseAssetType.Video);

          return (
            <div key={asset.id} className="asset-item">
              <div
                className={`tk-inline asset-row ${this.getAssetTypeClass(asset.assetType)} ${isClickable ? "clickable" : ""} ${isLocked ? "locked" : ""}`}
                onClick={isClickable ? () => this.handleAssetClick(asset) : undefined}
              >
                <div className="asset-icon-wrap">
                  <label
                    className={isLocked ? "mdi mdi-lock" : this.getAssetIconClass(asset.assetType)}
                  ></label>
                </div>
                <div className="asset-detail">
                  <div className="asset-title">
                    <label>{asset.title}</label>
                  </div>
                  <div className="asset-subtitle">
                    <label>{isLocked ? "خصوصی" : asset.fileSize}</label>
                  </div>
                </div>
                {asset.assetType === TrainingCourseAssetType.Video && !isLocked ? (
                  <div className="asset-arrow">
                    <label
                      className={`mdi ${isVideoOpen ? "mdi-chevron-up" : "mdi-chevron-down"}`}
                    ></label>
                  </div>
                ) : null}
              </div>

              {isVideoOpen ? this.videoPlayerView(asset) : null}
            </div>
          );
        })}
      </div>
    );
  }

  videoPlayerView(asset: TrainingCourseSessionAssetView) {
    const videoUrl = this.resolveAssetUrl(asset);
    if (!videoUrl) return null;

    return (
      <div className="video-player-wrap">
        <video
          ref={(el) => {
            this.videoRefs[asset.id] = el;
          }}
          className="session-video"
          src={videoUrl}
          controls
          playsInline
          controlsList="nodownload"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        <button
          type="button"
          className="fullscreen-btn"
          onClick={(e) => this.handleFullscreen(asset.id, e)}
        >
          <span className="mdi mdi-fullscreen"></span>
          تمام صفحه
        </button>
      </div>
    );
  }
}
